import { useState } from "react";
import { Eye, EyeOff, ChevronLeft, Info, AlertTriangle } from "lucide-react";
import Logo from "./Logo";
import LeftPanel from "./LeftPanel";

/* ─── PAGE HEADING ─────────────────────────────────────────── */
export function Head({ eyebrow, title, sub, ec = "var(--g600)" }) {
  return (
    <div className="fu" style={{ marginBottom: 28 }}>
      {eyebrow && (
        <div style={{
          fontSize: 10.5, fontWeight: 700, letterSpacing: "2.2px",
          textTransform: "uppercase", color: ec, marginBottom: 9,
        }}>{eyebrow}</div>
      )}
      <h1 style={{
        fontFamily: "'Playfair Display',serif",
        fontSize: "clamp(22px,3.5vw,28px)", fontWeight: 500,
        color: "var(--ink)", marginBottom: 6, lineHeight: 1.22,
      }}>{title}</h1>
      {sub && (
        <p style={{ fontSize: 13.5, color: "var(--mut)", fontWeight: 300, lineHeight: 1.6 }}>{sub}</p>
      )}
    </div>
  );
}

/* ─── FIELD INPUT ──────────────────────────────────────────── */
export function Field({
  label, type = "text", value, onChange, placeholder,
  icon: Ic, error, hint, autoFocus, maxLength, delay = "d1"
}) {
  const [show, setShow] = useState(false);
  const isPass = type === "password";
  const t = isPass ? (show ? "text" : "password") : type;

  return (
    <div className={`fu ${delay}`} style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
      <label style={{
        fontSize: 11, fontWeight: 700, letterSpacing: ".6px",
        textTransform: "uppercase", color: "var(--ink2)",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{label}</label>

      <div
        className={`cl-wrap${error ? " has-err" : ""}`}
        style={{
          display: "flex", alignItems: "center",
          background: "var(--wh)",
          border: `1.5px solid ${error ? "var(--err)" : "var(--ln)"}`,
          borderRadius: 10,
          transition: "border-color .2s,box-shadow .2s",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {Ic && (
          <span style={{
            paddingLeft: 12, color: "var(--mut)",
            display: "flex", alignItems: "center", flexShrink: 0,
          }}>
            <Ic size={15} strokeWidth={1.8} />
          </span>
        )}
        <input
          type={t}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          maxLength={maxLength}
          style={{
            flex: 1, minWidth: 0, width: 0,
            border: "none", outline: "none",
            padding: Ic ? "12px 10px 12px 8px" : "12px 13px",
            fontSize: 14.5,
            fontFamily: "'Outfit',sans-serif",
            background: "transparent", color: "var(--ink)",
          }}
        />
        {isPass && (
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            style={{
              background: "none", border: "none", padding: "0 11px",
              cursor: "pointer", color: "var(--mut)", display: "flex",
              alignItems: "center", transition: "color .2s", flexShrink: 0,
              minHeight: 44,
            }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--g600)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--mut)"}
          >
            {show ? <EyeOff size={16} strokeWidth={1.8} /> : <Eye size={16} strokeWidth={1.8} />}
          </button>
        )}
      </div>

      {error && (
        <span style={{ fontSize: 11.5, color: "var(--err)", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
          <Info size={11} />{error}
        </span>
      )}
      {hint && !error && (
        <span style={{ fontSize: 11.5, color: "var(--mut)", display: "flex", alignItems: "center", gap: 4 }}>
          <Info size={11} strokeWidth={1.8} />{hint}
        </span>
      )}
    </div>
  );
}

/* ─── PRIMARY BUTTON ───────────────────────────────────────── */
export function Btn({ label, loading, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      className="cl-btn"
      onClick={onClick}
      disabled={loading}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: "100%",
        padding: "14px 20px",
        background: loading
          ? "var(--g100)"
          : `linear-gradient(135deg,var(--g700),${hov ? "var(--g400)" : "var(--g500)"})`,
        color: loading ? "var(--mut)" : "#fff",
        border: "none", borderRadius: 10,
        fontSize: 15, fontWeight: 600,
        fontFamily: "'Outfit',sans-serif",
        letterSpacing: ".3px",
        cursor: loading ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
        minHeight: 48,
        boxShadow: loading ? "none"
          : hov ? "0 8px 26px rgba(46,125,50,.36)"
          : "0 4px 16px rgba(46,125,50,.24)",
        transition: "box-shadow .22s,background .22s,transform .15s",
        transform: hov && !loading ? "translateY(-1px)" : "none",
      }}
    >
      {loading ? (
        <>
          <span style={{
            width: 16, height: 16,
            border: "2px solid rgba(0,0,0,.1)", borderTopColor: "var(--mut)",
            borderRadius: "50%", animation: "spin .7s linear infinite", display: "inline-block",
          }} />
          Verifying…
        </>
      ) : label}
    </button>
  );
}

/* ─── ALERT ────────────────────────────────────────────────── */
export function Alert({ type = "info", children }) {
  const cfg = {
    info: { bg: "var(--g50)",   bd: "var(--g200)", I: Info,          c: "var(--g600)" },
    warn: { bg: "var(--wrnL)", bd: "#ffcc80",      I: AlertTriangle, c: "var(--wrn)"  },
  }[type];
  const AI = cfg.I;
  return (
    <div style={{
      background: cfg.bg, border: `1px solid ${cfg.bd}`,
      borderRadius: 9, padding: "11px 13px",
      display: "flex", gap: 9, alignItems: "flex-start",
    }}>
      <span style={{ color: cfg.c, flexShrink: 0, marginTop: 1 }}>
        <AI size={14} strokeWidth={2} />
      </span>
      <span style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.55 }}>{children}</span>
    </div>
  );
}

/* ─── MOBILE HEADER ────────────────────────────────────────── */
export function MobHeader({ onBack }) {
  return (
    <div className="mob-header">
      {onBack && (
        <button className="mob-back" onClick={onBack}>
          <ChevronLeft size={17} /> Back
        </button>
      )}
      <Logo compact />
    </div>
  );
}

/* ─── DESKTOP BACK BTN ─────────────────────────────────────── */
export function DesktopBack({ onClick }) {
  return (
    <button
      className="back-btn desktop-back"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "none", border: "none", cursor: "pointer",
        color: "var(--mut)", fontSize: 13.5, fontWeight: 500,
        fontFamily: "'Outfit',sans-serif",
        padding: "7px 11px 7px 5px",
        borderRadius: 8, marginBottom: 28,
        transition: "color .2s,background .2s",
      }}
    >
      <ChevronLeft size={16} /> Back to roles
    </button>
  );
}

/* ─── AUTH SHELL (Layout wrapper) ──────────────────────────── */
export function Shell({ role, onBack, children }) {
  return (
    <div className="auth-shell">
      <LeftPanel role={role} />
      <div className="auth-right">
        <div className="auth-form-wrap">
          <MobHeader onBack={onBack} />
          {onBack && <DesktopBack onClick={onBack} />}
          {children}
        </div>
      </div>
    </div>
  );
}